import { timezoneList } from 'config/timezone';
import dayjs from 'dayjs';

import { calculateOffset } from 'editor/components/SideBar/Qualify/limitPlayTimes/periodPlay/utils';

function getTimezoneOffset(timezone) {
    return (timezoneList.find(t => t.tzCode === timezone) || {}).utc || 'Z';
}

export function utcDTStr2UTC(dateStr = '') {
    return new Date(`${dateStr.replace(/\s/, 'T')}Z`).getTime();
}

// dayjs 在轉換成某時區的日期時間是貌似會被 DST 影響，目前 github issue 有好些這問題有關
// issue 見：https://github.com/iamkun/dayjs/issues?q=is%3Aissue+is%3Aopen+timezone
// 目前先以暴力計算方式解決
export function utcDTStr2TimezoneDTStr(dateStr = '', timezone, format = 'YYYY-MM-DD HH:mm:ss') {
    const [date = '', time = ''] = dateStr.split(' ');
    const offset =
        timezoneList.find(tz => String(tz.tzCode).toLowerCase() === String(timezone).toLowerCase())
            ?.utc || '+00:00';
    const method = offset[0] === '+' ? (a, b) => a + b : (a, b) => a - b;
    const { timeStr, dayoffset } = calculateOffset(offset, time, method);
    const timezoneDate = (dayoffset < 0
        ? dayjs(date).subtract(Math.abs(dayoffset), 'day')
        : dayjs(date).add(Math.abs(dayoffset), 'day')
    ).format('YYYY-MM-DD');

    return dayjs(`${timezoneDate} ${timeStr}`).format(format);
    // return utc2TimezoneDTStr(utcDTStr2UTC(dateStr), timezone, format);
}

export function utc2TimezoneDTStr(timeStamp, timezone, format = 'YYYY-MM-DD HH:mm:ss') {
    return dayjs(timeStamp).tz(timezone).format(format);
}

export function utcDTStr2LocalDTStr(dateStr = '', format) {
    return getDateTimeStr(`${dateStr.replace(/\s/, 'T')}Z`, format);
}

export function getCurrentUtcDTStr() {
    return new Date().toISOString().replace('T', ' ').split('.')[0];
}

export function getCurrentUtcDateStr() {
    return new Date().toISOString().split('T')[0];
}

/**
 * 判斷 UTC 日期是否在現在之後
 * @param {String} dateStr - UTC 日期字串
 * @returns {Boolean} 是否在現在之後
 */
export function isUtcDateAfterNow(dateStr) {
    return dayjs(dateStr).isAfter(dayjs(), 'day');
}

export function timezoneDTStr2UTC(dateStr = '', timezone) {
    const tzOffset = getTimezoneOffset(timezone);
    return new Date(`${dateStr.replace(/\s/, 'T')}${tzOffset}`).getTime();
}

export function timezoneDTStr2UtcDTStr(dateStr, timezone) {
    return timeStamp2UtcDtStr(timezoneDTStr2UTC(dateStr, timezone));
}

export function timeStamp2UtcDtStr(timestamp) {
    const [str] = new Date(timestamp).toISOString().split('.');
    return str.replace('T', ' ');
}

export function timezoneDTStr2LocalDTStr(dateStr = '', timezone, format) {
    const tzOffset = getTimezoneOffset(timezone);
    return getDateTimeStr(`${dateStr.replace(/\s/, 'T')}${tzOffset}`, format);
}

export function getDayRange(date1, date2) {
    const d1 = dayjs(date1);
    return Math.abs(d1.diff(date2, 'day'));
}

export function hasUtcDTStartInLocal(startDate) {
    return Date.now() >= utcDTStr2UTC(startDate);
}

export function hasUtcDTEndInLocal(endDate) {
    return Date.now() >= utcDTStr2UTC(endDate);
}

export function getLocalTimezone() {
    return dayjs.tz.guess();
}

export function getDateTimeStr(dateData, format = 'YYYY-MM-DD HH:mm:ss') {
    return dayjs(dateData).format(format);
}

export function dateTimeStrIsLocalToday(dateStr) {
    return dayjs(timezoneDTStr2UTC(dateStr)).isToday();
}

export function isLocalSameDateInPromoTimezone(dateStr, timezone) {
    const dateStrInPromoTimezone = utcDTStr2TimezoneDTStr(dateStr, timezone, 'YYYY-MM-DD');
    const localDateStrInPromoTimezone = utcDTStr2TimezoneDTStr(
        getCurrentUtcDTStr(),
        timezone,
        'YYYY-MM-DD'
    );
    return dateStrInPromoTimezone === localDateStrInPromoTimezone;
}

export function removeSecondsFromDTStr(dateStr) {
    return dayjs(dateStr).format('YYYY-MM-DD HH:mm');
}

export function isBefore(a, b) {
    return dayjs(a).isBefore(dayjs(b));
}

export function UTC_toLocalTime(timeString, format) {
    if (format) return dayjs(timeString).utc(true).local().format(format);
    return dayjs(timeString).utc(true).local();
}

/**
 * 判斷日期是否在時間區間內
 * @param {String} dateToCheck - 要檢查的日期
 * @param {String} startDate - 開始日期
 * @param {String} endDate - 結束日期
 * @param {String} timezone - 時區
 * @param {Boolean} inclusive - 是否包含開始和結束日期
 * @returns {Boolean} 是否在時間區間內
 */
export function isDateInTimeRange(
    dateToCheck,
    startDate,
    endDate,
    timezone = null,
    inclusive = true
) {
    // 處理無效日期
    if (!dateToCheck || !startDate || !endDate) {
        return false;
    }

    try {
        // 如果有時區，轉換日期到指定時區
        if (timezone) {
            dateToCheck = utcDTStr2TimezoneDTStr(dateToCheck, timezone);
            startDate = utcDTStr2TimezoneDTStr(startDate, timezone);
            endDate = utcDTStr2TimezoneDTStr(endDate, timezone);
        }

        // 使用 dayjs 處理日期比較
        const dateToCheckObj = dayjs(dateToCheck);
        const startDateObj = dayjs(startDate);
        const endDateObj = dayjs(endDate);

        // 檢查日期是否有效
        if (!dateToCheckObj.isValid() || !startDateObj.isValid() || !endDateObj.isValid()) {
            return false;
        }

        if (inclusive) {
            return (
                (dateToCheckObj.isSame(startDateObj) || dateToCheckObj.isAfter(startDateObj)) &&
                (dateToCheckObj.isSame(endDateObj) || dateToCheckObj.isBefore(endDateObj))
            );
        } else {
            return dateToCheckObj.isAfter(startDateObj) && dateToCheckObj.isBefore(endDateObj);
        }
    } catch (error) {
        console.error('Error in isDateInTimeRange:', error);
        return false;
    }
}
/**
 * 檢查指定時間是否已過期
 * @param {string} dateTimeString - UTC 時間字串，格式為 "YYYY-MM-DD HH:mm:ss"
 * @param {string} timezone - 時區，預設為 "Asia/Taipei"
 * @param {number} delayHours - 延遲小時數，預設為 1 小時
 * @returns {boolean} - 如果時間已過期則返回 true
 */
export const isTimeExpired = (dateTimeString, timezone = 'Asia/Taipei', delayHours = 1) => {
    if (!dateTimeString) return false;

    try {
        // 使用 dayjs 解析 UTC 時間字串並轉換為指定時區
        const utcDateTime = dayjs.utc(dateTimeString);
        const currentTime = dayjs();

        // 檢查解析是否成功
        if (!utcDateTime.isValid()) {
            console.warn('無法解析 UTC 時間字串:', dateTimeString);
            return false;
        }

        // 將 UTC 時間轉換為指定時區
        const targetTimeInTimezone = utcDateTime.tz(timezone);

        // 計算延遲後的截止時間（活動結束時間 + 延遲小時數）
        const delayedEndTime = targetTimeInTimezone.add(delayHours, 'hour');

        // 比較當前時間與延遲後的截止時間
        return currentTime.isAfter(delayedEndTime);
    } catch (error) {
        console.error('時間比較時發生錯誤:', error);
        return false;
    }
};
